import React, { useEffect, useMemo } from 'react';
import { AnyObject, Form, FormSpy, useForm } from 'react-final-form';
import { useHistory, useLocation } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/browser';
import { FormApi } from 'final-form';

import useFeatureFlag from '@common/utils/hooks/useFeatureFlag';
import { SELF_SERVICE_PATH } from '@utils/constants';

import {
  ActionType,
  Flow,
  FormEffect,
  FormState,
  FormValidation,
  useBookingContext,
} from '../../../_common/context/BookingContext';
import { AutoSave } from '../../../_common/forms/AutoSave';
import { CenteredSpinner, SpinnerSizes } from '../../../_common/Spinner';
import { Heading1, Heading4, InfoText } from '../../../_common/Typography';
import { trimStringValues } from '../../../_common/utils/form';
import { useConfirm } from '../../../_common/utils/hooks/useConfirm';
import { useOneTimeToken } from '../../../_common/utils/hooks/useOneTimeToken';
import useQueryParams from '../../../_common/utils/hooks/useQueryParams';
import { useScopedTranslation } from '../../../_common/utils/hooks/useScopedTranslation';
import {
  AttributesQuery,
  FieldsListQuery,
  UpdateContext,
} from '../../../types/api';
import { FormOverlay } from '../_common/Components';
import FormEffectWatcher from '../_common/FormEffectWatcher';
import { createSchema } from '../utils/validator';

import CollapsedForm from './_common/CollapsedForm';
import SectionedForm from './_common/SectionedForm';
import SwitchCategoryWatcher from './_common/SwitchCategoryWatcher';
import { getDirtyFields } from './utils/getDirtyFields';
import { mapFieldDefinitions, mapInitialValues } from './utils/mappers';
import AddressWatcher from './AddressWatcher';
import { UPDATE_CONTEXT } from './mutations';
import { ATTRIBUTES, FIELDS_LIST, LEAD_SEGMENT } from './queries';

export default function CustomerForm() {
  const { t } = useScopedTranslation();
  const { id: contextId } = useQueryParams();
  const history = useHistory();
  const { pathname } = useLocation();
  const { ConfirmModal, confirm } = useConfirm();
  const authorizeFromContext = useOneTimeToken();
  const isListingsEnabled = useFeatureFlag('steps.listings.enabled');
  const [
    {
      categoryId,
      customerFormState,
      customerFormSubmissionCount,
      customerFormValidation,
      customerValid,
    },
    dispatch,
  ] = useBookingContext();

  const { data: contextData, loading: contextLoading } =
    useQuery<AttributesQuery>(ATTRIBUTES, {
      variables: { contextId },
      skip: !contextId,
      notifyOnNetworkStatusChange: true,
    });

  const { data: fieldsListData, loading: fieldsListLoading } =
    useQuery<FieldsListQuery>(FIELDS_LIST, {
      variables: { categoryId, active: true },
      skip: !categoryId,
      fetchPolicy: 'no-cache',
    });

  const fields = useMemo(
    () => fieldsListData?.category?.fields ?? [],
    [fieldsListData],
  );
  const usedForQualificationFields = useMemo(
    () => fields.filter((f) => f.usedForQualification),
    [fields],
  );

  const [updateContext, { loading: updateLoading }] =
    useMutation<UpdateContext>(UPDATE_CONTEXT, {
      update: async (cache, { data }) => {
        if (
          data?.updateContext?.contextId != null &&
          data?.updateContext?.contextId !== ''
        ) {
          const { contextId, leadSegment } = data.updateContext;

          await authorizeFromContext(contextId);

          history.push(`?id=${contextId}`);

          if (leadSegment?.id) {
            cache.writeQuery({
              query: LEAD_SEGMENT,
              variables: { contextId },
              data: {
                leadSegment: {
                  ...leadSegment,
                  __typename: 'LeadSegment',
                },
              },
            });
          }
        } else {
          Sentry.withScope((scope) => {
            scope.setExtra('data', data);
            scope.setExtra('pathname', pathname);
            Sentry.captureException(new Error('Empty context id in response'));
          });
        }
      },
    });

  const fieldDefinitions = useMemo(() => {
    return mapFieldDefinitions(fields);
  }, [fields]);

  const qualificationValidationSchema = useMemo(() => {
    return createSchema(
      fields.map((field) => ({
        ...field,
        required: field.requiredForQualification,
      })),
      t,
    );
  }, [fields, t]);

  const validationSchema = useMemo(() => {
    return createSchema(fields, t);
  }, [fields, t]);

  const usedValidationSchema = useMemo(() => {
    return customerFormValidation === FormValidation.PARTIAL
      ? qualificationValidationSchema
      : validationSchema;
  }, [customerFormValidation, qualificationValidationSchema, validationSchema]);

  const initialValues = useMemo(() => {
    return mapInitialValues(fields, contextData);
  }, [fields, contextData]);

  async function handleValidateCustomer(values: any) {
    try {
      if (!validationSchema) {
        dispatch({
          type: ActionType.SET_CUSTOMER_VALID,
          payload: true,
        });

        return true;
      }

      await validationSchema.validate(trimStringValues(values), {
        abortEarly: false,
      });

      dispatch({
        type: ActionType.SET_CUSTOMER_VALID,
        payload: true,
      });

      return true;
    } catch (error) {
      dispatch({
        type: ActionType.SET_CUSTOMER_VALID,
        payload: false,
      });

      return false;
    }
  }

  async function handleSubmit(values: AnyObject, form: FormApi) {
    let shouldRefetchQueries = customerFormSubmissionCount === 0 && !!contextId;

    if (customerFormSubmissionCount > 0) {
      const dirtyFields = getDirtyFields(
        values,
        form.getState().initialValues,
        usedForQualificationFields.map((field) => field.id),
      );

      if (dirtyFields.length > 0) {
        if (await confirm(t('customer_form.qualification_field_change'))) {
          shouldRefetchQueries = true;
        } else {
          return dispatch({
            type: ActionType.DO_FORM_EFFECT,
            payload: FormEffect.RESET,
          });
        }
      }
    }

    const trimmedValues = trimStringValues(values);
    const category = fieldsListData?.category?.key;
    const attributes = (fields || []).map(({ id, key }) => ({
      key: key,
      value: String(trimmedValues[id] ?? ''),
    }));

    await updateContext({
      variables: {
        input: {
          contextId: contextId?.trim() ? contextId : null,
          attributes,
          data: category != null ? JSON.stringify({ category }) : undefined,
        },
      },
      refetchQueries: () =>
        shouldRefetchQueries
          ? [
              'AttributesQuery',
              'CallbackQuery',
              'SubjectsQuery',
              ...(isListingsEnabled ? ['ListingsQuery'] : []),
            ]
          : [],
    });

    await handleValidateCustomer(values);

    dispatch({
      type: ActionType.DO_FORM_EFFECT,
      payload: FormEffect.SUBMIT,
    });

    // Reset flow on submit (needed when having switched categories)
    dispatch({
      type: ActionType.SET_FLOW,
      payload: pathname.includes(SELF_SERVICE_PATH)
        ? Flow.SELF_SERVICE
        : Flow.BOOKING,
    });

    if (customerFormState !== FormState.COLLAPSED) {
      dispatch({
        type: ActionType.SET_CUSTOMER_FORM_STATE,
        payload: FormState.COLLAPSED,
      });
      window.scrollTo(0, 0);
    }
  }

  useEffect(() => {
    if (contextId == null) {
      dispatch({
        type: ActionType.SET_CUSTOMER_FORM_STATE,
        payload: FormState.EXPANDED,
      });
    }
  }, [contextId, dispatch]);

  const isLoading = fieldsListLoading || contextLoading;

  if (isLoading) {
    return <CenteredSpinner size={SpinnerSizes.LARGE} />;
  }

  if (!isLoading && fields.length === 0) {
    return (
      <div className="flex w-full h-full">
        <div className="m-auto text-center">
          <Heading4>{t('customer_form.no_fields.title')}</Heading4>
          <InfoText>{t('customer_form.no_fields.text')}</InfoText>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col overflow-y-auto h-full ${
        customerFormState === FormState.COLLAPSED
          ? 'bg-gray-100 border-r border-gray-300 w-0 lg:w-84'
          : 'bg-white w-full'
      } ${updateLoading ? 'pointer-events-none' : 'pointer-events-auto'}`}
    >
      <div className="hidden">
        <Heading1>{t('customer_form.title')}</Heading1>
      </div>
      {customerFormState === FormState.COLLAPSED && updateLoading && (
        <FormOverlay />
      )}
      <ConfirmModal />
      <SwitchCategoryWatcher />
      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
        subscription={{ errors: true, dirty: true, valid: true }}
      >
        {({ handleSubmit: finalFormHandleSubmit }) => (
          <form className="px-3 py-5" onSubmit={finalFormHandleSubmit}>
            <FormEffectWatcher />
            <AddressWatcher fields={fields} />

            {customerFormState === FormState.EXPANDED ? (
              <SectionedForm
                fieldDefinitions={fieldDefinitions}
                submitting={updateLoading}
                validationSchema={usedValidationSchema}
                validationType={customerFormValidation}
              />
            ) : (
              <>
                <CollapsedForm
                  fieldDefinitions={fieldDefinitions}
                  submitting={updateLoading}
                  validationSchema={usedValidationSchema}
                  validationType={customerFormValidation}
                />
                <AutoSave />
              </>
            )}
          </form>
        )}
      </Form>
    </div>
  );
}
